import { createContext, useContext, FC } from "react";
import { Box, Button, ModalBody, useDisclosure } from "@chakra-ui/react";
import { Modal } from "components/modals";
import { StripeUpdateCardForm } from "./stripeUpdateCardForm";
import { useAppSelector } from "lib/redux";
import { selectMe } from "domains/account";
import { PermissionLevels } from 'lib/types';
import { useTeam, useToastNotification } from "lib/hooks";

const UpgradeContext = createContext(null);

export const UpgradeProvider: FC = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const me = useAppSelector(selectMe);
  const { successToast } = useToastNotification();
  const { mutate } = useTeam();

  const handleFinish = async () => {
    onClose();
    await mutate();
    successToast({title: 'Success! Your invoice has been updated'});
  }

  const adminModal = (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upgrade to finish this pack."
      size="lg"
    >
      <ModalBody>
        <Box>
          Congrats - you’ve have successfully been using Packs for the entire free trial period. To keep organizing your digital life in Packs, you can easily upgrade.
        </Box>
        <Box pt="20px">
          The <a href="/pricing.html" target="_blank" style={{textDecoration: 'underline'}}>next tier</a> is just $10 / team member / month.
        </Box>
        <Box paddingBottom="20px">
          <StripeUpdateCardForm onFinish={handleFinish} label="Upgrade" />
        </Box>
      </ModalBody>
    </Modal>
  );

  const isMember = !!me.permissions;

  const nonAdminModal = (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Your Team Admin needs to add assets"
      size="lg"
    >
      <ModalBody>
        <Box paddingBottom="20px">
          It looks like you and your teammates have used up all available assets on your team account. Your admin needs to upgrade your team’s plan. 
        </Box>
        <Box paddingBottom="40px">
          <strong>We’ve sent your Team Admin an email to let them know.</strong> But to keep using Packs sans interruption, you may want to contact them directly.
        </Box>
        {isMember && (
          <Box paddingBottom="20px" display="flex" alignItems="center" flexDirection="column">
            <Box paddingBottom="10px">
              Not sure who your Team Admin is?
            </Box>
            <Button
              type="submit"
              variant="primary"
              onClick={() => window.open('/team', '_blank')}
            >
              View your team here
            </Button>
          </Box>
        )}
      </ModalBody>
    </Modal>
  );

  const isAdmin = me.permissions === PermissionLevels.ADMIN || me.permissions === PermissionLevels.OWNER;

  return (
    <UpgradeContext.Provider value={{ openUpgradeModal: onOpen }}>
      {children}
      {isAdmin ? adminModal : nonAdminModal}
    </UpgradeContext.Provider>
  )
}

export const useUpgradeContext = () => useContext(UpgradeContext);